import { request } from '@umijs/max';
import type { CommonResult } from '@/services/api/types/common';

/**
 * 与 xPredictorWeb（合规风控）一致：/client/compliance/auth/*
 * 可选覆盖：REACT_APP_COMPLIANCE_AUTH_PREFIX
 */
export const COMPLIANCE_AUTH_PREFIX =
  (typeof process !== 'undefined' && process.env.REACT_APP_COMPLIANCE_AUTH_PREFIX) ||
  '/client/compliance/auth';

export type ComplianceLoginBody = {
  email: string;
  password: string;
};

export type ComplianceLoginData = {
  token?: string;
  userId?: string;
  name?: string;
  email?: string;
  roles?: string[];
};

export type ComplianceUserInfoData = {
  userId?: string;
  username?: string;
  email?: string;
  roles?: string[];
};

function isCommonResultSuccess<T>(res: CommonResult<T>): boolean {
  if (res?.status === '1' || res?.status === '200' || res?.status === 'success') {
    return true;
  }
  if (res?.status === '0' || res?.status === 'error') {
    return false;
  }
  if (res?.success === false) {
    return false;
  }
  if (res?.data !== undefined && res?.data !== null) {
    return true;
  }
  return false;
}

export function unwrapCommonResult<T>(res: CommonResult<T>, fallbackMessage = '请求失败'): T {
  if (!isCommonResultSuccess(res)) {
    throw new Error((res?.message as string) || fallbackMessage);
  }
  return res.data as T;
}

/** POST /client/compliance/auth/login */
export async function postComplianceLogin(
  body: ComplianceLoginBody,
  options?: Record<string, unknown>,
) {
  const res = await request<CommonResult<ComplianceLoginData>>(`${COMPLIANCE_AUTH_PREFIX}/login`, {
    method: 'POST',
    data: body,
    skipErrorHandler: true,
    ...(options || {}),
  });
  return unwrapCommonResult(res, '登录失败');
}

/** POST /client/compliance/auth/logout */
export async function postComplianceLogout(options?: Record<string, unknown>) {
  const res = await request<CommonResult<string>>(`${COMPLIANCE_AUTH_PREFIX}/logout`, {
    method: 'POST',
    skipErrorHandler: true,
    ...(options || {}),
  });
  try {
    return unwrapCommonResult(res, '登出失败');
  } catch {
    return res?.data;
  }
}

/** POST /client/compliance/auth/register（query：email、password、username） */
export async function postComplianceRegister(
  params: { email: string; password: string; username: string },
  options?: Record<string, unknown>,
) {
  const res = await request<CommonResult<string>>(`${COMPLIANCE_AUTH_PREFIX}/register`, {
    method: 'POST',
    params,
    skipErrorHandler: true,
    ...(options || {}),
  });
  return unwrapCommonResult(res, '注册失败');
}

/** GET /client/compliance/auth/userinfo */
export async function getComplianceUserInfo(options?: Record<string, unknown>) {
  const res = await request<CommonResult<ComplianceUserInfoData>>(
    `${COMPLIANCE_AUTH_PREFIX}/userinfo`,
    {
      method: 'GET',
      skipErrorHandler: true,
      ...(options || {}),
    },
  );
  return unwrapCommonResult(res, '获取用户信息失败');
}
